// ============================================================================
// offlineFlag.ts — SSoT офлайн-СОСТОЯНИЯ клиента (SSoT/SRP).
//
//   forced   — «Автономный режим» (галочка в Профиле): связь с сервером
//              СОЗНАТЕЛЬНО отключена — всё читается из кэша, всё пишется в
//              очередь; хранится на устройстве (переживает перезагрузку);
//   linkDown — транспортный сбой (связь потеряна): ставит connection.ts по
//              статусу соединения (живой стрим EventBus), снимается ТОЛЬКО
//              подтверждённым online.
//
// Единая точка для: dataService (запись), persistedCache (срок давности не
// отменяет данные офлайн), offlineCache (чтение через кэш), connection.ts
// (статус), main.tsx (включение/выключение шины).
// ============================================================================

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'hydra_offline_mode';

let forced = false;
let linkDown = false;
let inited = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      // слушатели не должны ронять состояние
    }
  }
}

/** Инициализация (main.tsx, до остальных инициаторов): восстановить флаг. */
export function initOfflineFlag(): void {
  if (inited) return;
  inited = true;
  try {
    forced = localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    forced = false;
  }
}

/** «Автономный режим» включён пользователем (без связи). */
export function isForcedOffline(): boolean {
  return forced;
}

/** Включить/выключить «Автономный режим» (хранится на устройстве). */
export function setForcedOffline(on: boolean): void {
  if (forced === on) return;
  forced = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // приватный режим — флаг живёт до перезагрузки
  }
  notify();
}

/** Транспортный сбой: связь с сервером потеряна (ставит connection.ts). */
export function markLinkDown(down: boolean): void {
  if (linkDown === down) return;
  linkDown = down;
  notify();
}

/** Связь фактически недоступна (автономный режим ИЛИ сеть/сервер недоступны). */
export function isEffectivelyOffline(): boolean {
  if (forced) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return linkDown;
}

/** Подписка на смену офлайн-состояния (без таймеров). */
export function subscribeOfflineFlag(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Реактивное чтение флага «Автономный режим» (индикаторы/переключатели). */
export function useForcedOffline(): boolean {
  return useSyncExternalStore(
    (cb) => subscribeOfflineFlag(cb),
    () => forced,
  );
}
